import { mapState, mapStores } from 'pinia';
import { defineComponent, h, VNode } from 'vue';
import { NButton, NDatePicker, NDropdown, NEllipsis, SelectOption } from 'naive-ui';

import { useStationDetailStore } from '~/store/station-details.store';

import ComponentBase from '~/components/component-base';

import { useStationStore } from '~/store/station.store';
import { Station } from '~/api/models/station.model';
import { AvoltaAlarm } from '~/configs/alarms/avolta-alarm.model';
import { getStationAlarms, isStationInGasAlarm, isStationInTrouble, isStationOffline } from '~/misc/station-utils';
import moment, { Moment } from 'moment';
import ExportDataModal from '~/components/modals/export-data-modal/export-data-modal.vue';

export default defineComponent({

    extends: ComponentBase,

    components: {
        NButton,
        NDropdown,
        NEllipsis,
        NDatePicker,

        ExportDataModal,
    },

    data() {


        const data = {
            shortcuts: {},
            timerange:  [0,0],
        };

        const now = moment().unix() * 1000;
        data.shortcuts[this.$t('advanced_view.last_month')] = [moment().subtract(1, 'month').unix() * 1000, now];
        data.shortcuts[this.$t('advanced_view.last_6_month')] = [moment().subtract(6, 'month').unix() * 1000, now];
        data.shortcuts[this.$t('advanced_view.last_quarter')] = [moment().subtract(3, 'month').unix() * 1000, now];
        data.shortcuts[this.$t('advanced_view.last_year')] = [moment().subtract(12, 'month').unix() * 1000, now];

        return data;
    },

    mounted() {
        this.buildTimerange();
    },

    computed: {
        ...mapStores(useStationDetailStore),
        ...mapState(useStationDetailStore, ['station', 'since', 'before', 'loading']),
        ...mapState(useStationStore, ['stations', 'group']),

        previous(): Station {
            const stations: Station[] = this.stations;
            const index: number = stations.findIndex(s => s?.id === this.station?.id);

            if(index === -1) {
                return;
            }

            const nextIndex = index <= 0 ? stations?.length - 1 : index - 1;
            return this.stations[nextIndex];
        },

        next(): Station {
            const stations: Station[] = this.stations;
            const index: number = stations.findIndex(s => s?.id === this.station?.id);

            if(index === -1) {
                return;
            }

            const nextIndex = (index + 1) % stations.length;
            return this.stations[nextIndex];
        },

        options(): SelectOption[] {
            return this.stations.map(station => {

                return { 
                    value: station?.id,
                    label: station?.name,
                    station,
                }
            });
        },

    },

    methods: {

        async refresh(): Promise<void> {
            try {
                await this.stationDetailsStore?.refresh();
            } catch(error) {
                this.$notifyError(this.$t('device_details.refresh_error'));
            }
        },

        renderLabel(option: SelectOption): VNode {
            const station: Station = option.station as Station;
            const alarms: AvoltaAlarm[] = getStationAlarms(station);

            const isOffline: boolean = isStationOffline(station);
            const isGasAlarm: boolean = isStationInGasAlarm(station);
            const isTroubleAlarm: boolean = isStationInTrouble(station);
            
            return h('div',  { class: { 'has-text-danger': isGasAlarm, 'has-text-warning': !isGasAlarm && (isTroubleAlarm || isOffline)}}, [
                h(
                    'span', 
                    null,
                    [
                        h(
                            NEllipsis, 
                            { style: {'max-width': '200px'}, tooltip: false}, 
                            { default: () => station?.name }
                        )
                    ]),
                ...alarms.map(a => h('span', { class: a?.icon }, null))
            ]) 
        },
        
        selectDevice(a, selected: SelectOption) {
            const station: Station = selected?.station as Station;
            if(!station?.id) {
                return;
            }

            this.$router.replace({ params: { station: station?.id}});
        },

        setMonthRange(range): void {
            const [start, end]: [number, number] = range;

            const since: Moment = moment.unix(start / 1000);
            const before: Moment = moment.unix(end / 1000).add(1, 'day');

            this.$router.replace({
                query: {
                    since: since.format(),
                    before: before.format(),
                }
            });
        },

        isRangeDateDisabled(ts: number, type: 'start' | 'end', range: [number, number] | null): boolean {
            if(moment.unix((ts / 1000)).isAfter(moment())) {
                return true;
            }

            if(!range) {
                return false;
            }

            return moment.unix(range[0] / 1000).diff(moment.unix(range[1] / 1000), 'months') > 12;
        },

        buildTimerange(): [number, number] {

            if(!this.since || !this.before) {
                return [0,0];
            }

            this.timerange = [
                this.since.unix() * 1000,
                this.before.unix() * 1000,
            ];
        },
        
    },

    watch: {
        since() {
            this.buildTimerange();
        },

        before() {
            this.buildTimerange();
        }
    }
});